import React, { useEffect, useState } from "react";
import {
  downloadNoteAttachment,
  getAttachmentMimeType,
  getAttachmentPreviewKind,
  getAttachmentPreviewObjectUrl,
  renderDocxPreviewHtml,
} from "../../utils/noteAttachments";

function NoteAttachmentPreviewModal({ attachment, onClose }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [docxHtml, setDocxHtml] = useState("");
  const [mimeType, setMimeType] = useState("");
  const [previewKind, setPreviewKind] = useState("none");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let revoke = () => {};

    const loadPreview = async () => {
      const kind = getAttachmentPreviewKind(attachment);
      setPreviewKind(kind);

      if (kind === "none") {
        setError("Preview is not available for this file type.");
        setLoading(false);
        return;
      }

      if (kind === "legacy-doc") {
        setLoading(false);
        return;
      }

      try {
        if (kind === "docx") {
          const html = await renderDocxPreviewHtml(attachment);
          setDocxHtml(html);
          setMimeType(getAttachmentMimeType(attachment));
          return;
        }

        const preview = await getAttachmentPreviewObjectUrl(attachment);
        revoke = preview.revoke;
        setPreviewUrl(preview.url);
        setMimeType(preview.mimeType || getAttachmentMimeType(attachment));
      } catch (loadError) {
        console.error("Failed to load attachment preview", loadError);
        setError("Could not open this attachment. Try downloading it instead.");
      } finally {
        setLoading(false);
      }
    };

    loadPreview();

    return () => {
      revoke();
    };
  }, [attachment]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadNoteAttachment(attachment);
    } catch (downloadError) {
      console.error("Failed to download attachment", downloadError);
      window.alert("Could not download this file. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const isPdf = previewKind === "pdf" && mimeType === "application/pdf";
  const isImage = previewKind === "image" && mimeType.startsWith("image/");
  const isDocx = previewKind === "docx";
  const isLegacyDoc = previewKind === "legacy-doc";

  return (
    <div
      className="my-notes-attachment-preview-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="my-notes-attachment-preview-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="attachment-preview-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="my-notes-attachment-preview-header">
          <h2 id="attachment-preview-title">{attachment.fileName}</h2>
          <div className="my-notes-attachment-preview-actions">
            <button
              type="button"
              className="my-notes-attachment-preview-download"
              onClick={handleDownload}
              disabled={downloading}
            >
              <img src="../assets/images/download.svg" alt="Download" width={17} height={17}/>
              {/* {downloading ? "Downloading..." : "Download"} */}
            </button>
            <button
              type="button"
              className="my-notes-modal-close"
              aria-label="Close preview"
              onClick={onClose}
            >
              ×
            </button>
          </div>
        </div>

        <div className="my-notes-attachment-preview-body">
          {loading ? <p className="my-notes-attachment-preview-status">Loading preview...</p> : null}
          {error ? <p className="my-notes-attachment-preview-status is-error">{error}</p> : null}

          {isLegacyDoc && !loading ? (
            <div className="my-notes-attachment-preview-legacy-doc">
              <p>
                Older <strong>.doc</strong> files cannot be previewed in the browser. Download
                the file and open it in Microsoft Word or another document app.
              </p>
              <button
                type="button"
                className="my-notes-view-attachment-btn"
                onClick={handleDownload}
                disabled={downloading}
              >
                {downloading ? "Downloading..." : "Download attachment"}
              </button>
            </div>
          ) : null}

          {!loading && !error && previewUrl && isPdf ? (
            <iframe
              title={attachment.fileName}
              src={previewUrl}
              className="my-notes-attachment-preview-frame"
            />
          ) : null}

          {!loading && !error && previewUrl && isImage ? (
            <img
              src={previewUrl}
              alt={attachment.fileName}
              className="my-notes-attachment-preview-image"
            />
          ) : null}

          {!loading && !error && isDocx && docxHtml ? (
            <div
              className="my-notes-attachment-preview-docx"
              dangerouslySetInnerHTML={{ __html: docxHtml }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default NoteAttachmentPreviewModal;
